import * as React from 'react'
import { useState } from 'react'

import { Button, Table, Text } from '@factorialco/deprecated-design-system'
import { FinanceCostCentersSelectorOptionsDocument } from 'generated/resources/graphql'

import { usePaginatedQuery } from 'modules/core/hooks/usePaginatedQuery'
import i18n from 'modules/core/lib/i18n'

import { ValueOption } from 'modules/finance/components/Dimensions/SetAssignmentsPanel/DimensionValuePicker/Option'

import ValueRow, { ValueAssignment } from './ValueRow'

interface Props {
  options: ValueOption[]
  values: ValueAssignment[]
  legalEntityId: number
  onSearchChange?: (search: string) => void
  onChange: (values: ValueAssignment[]) => void
}

const DimensionMultipleValuePicker = ({ options, values, onChange, legalEntityId }: Props) => {
  const handleValueDeleted = (value: ValueOption) => {
    onChange([...values.filter((v) => v.id !== value.id)])
  }

  const [search, setSearch] = useState('')
  const handleSearchChange = (searchValue: string) => {
    setSearch(searchValue)
  }
  const { nodes, controls } = usePaginatedQuery(FinanceCostCentersSelectorOptionsDocument, {
    variables: {
      legalEntityId: legalEntityId,
      search: search,
    },
    extractConnection: (data) => data.finance.costCentersConnection,
  })

  const nodeOptions: ValueOption[] = nodes.map(
    (node): ValueOption => ({ id: `${node.id}`, name: node.name })
  )

  const filteredNodeOptions = nodeOptions.filter((op) => !values.find((v) => v.id === op.id))

  const addRow = () => {
    onChange([...values, { ...filteredNodeOptions[0]!, percentage: 0 }])
  }

  const handleValueChanged = (valueId: string, newValue: ValueAssignment) => {
    onChange(values.map((v) => (v.id == valueId ? newValue : v)))
  }

  const totalDedication = (values.reduce((acc, cur) => acc + cur.percentage, 0) * 100).toFixed(2)
  const notValid = totalDedication !== '100.00'

  return (
    <>
      <Table>
        <Table.Head>
          <Table.Row>
            <Table.HeadCell ellipsis wide>
              {i18n.t('finance.employee_dimensions_info.modals.set_assignments.value')}
            </Table.HeadCell>
            <Table.HeadCell ellipsis wide>
              {i18n.t('finance.employee_dimensions_info.modals.set_assignments.dedication')}
            </Table.HeadCell>
            <Table.HeadCell width={40} wide />
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {values.length === 0 ? (
            <Table.Blank
              text={i18n.t('finance.employee_dimensions_info.modals.set_assignments.no_records')}
              colSpan={3}
            />
          ) : (
            <>
              {values.map((value) => (
                <ValueRow
                  key={value.id}
                  value={value}
                  options={filteredNodeOptions}
                  controls={controls}
                  onChange={handleValueChanged}
                  onDelete={handleValueDeleted}
                  onSearchChange={handleSearchChange}
                />
              ))}
              <Table.Row background="grey200">
                <Table.Cell wide>
                  <Text.Normal size="200" weight="semibold" color="grey1000">
                    {i18n.t('finance.employee_dimensions_info.modals.set_assignments.total')}
                  </Text.Normal>
                </Table.Cell>
                <Table.Cell wide>
                  <Text.Normal
                    size="200"
                    weight="semibold"
                    color={notValid ? 'radicalRed1200' : 'grey1000'}
                  >
                    {totalDedication}
                  </Text.Normal>
                </Table.Cell>
                <Table.Cell />
              </Table.Row>
            </>
          )}
        </Table.Body>
      </Table>
      <Button
        onClick={addRow}
        type="secondary"
        disabled={filteredNodeOptions.length == 0}
        iconName="add"
      >
        {i18n.t('finance.employee_dimensions_info.modals.set_assignments.buttons.add')}
      </Button>
    </>
  )
}

export default DimensionMultipleValuePicker
